/**
 * Grid Reference Table Loader
 *
 * Manages loading of reference tables (master data) for grid columns.
 * Only loads tables for columns that are actually visible to optimize performance.
 */

import { FIELD_USER } from '../constants/fields';

/**
 * Get required table IDs from column definitions, filtered by visibility
 *
 * @param {Array} columnDefinitions - Array of column definition objects
 * @param {Object} columnVisibilityModel - Object mapping column names to visibility boolean
 * @returns {Array} Array of unique table IDs that need to be loaded
 */
export const getRequiredTableIds = (columnDefinitions, columnVisibilityModel = {}) => {
  if (!columnDefinitions || columnDefinitions.length === 0) {
    return [];
  }

  const tableIds = new Set();

  columnDefinitions.forEach(col => {
    // Only include reference tables for visible columns
    // If columnVisibilityModel is empty, assume all columns are visible
    const isVisible = Object.keys(columnVisibilityModel).length === 0
      || columnVisibilityModel[col.value] !== false;

    if (col.reference && isVisible) {
      tableIds.add(col.reference);
    }
  });

  return Array.from(tableIds);
};

/**
 * Check if any visible columns are user fields
 *
 * @param {Array} columnDefinitions - Array of column definition objects
 * @param {Object} columnVisibilityModel - Object mapping column names to visibility boolean
 * @returns {boolean} True if any visible column is a user field
 */
export const hasUserFields = (columnDefinitions, columnVisibilityModel = {}) => {
  if (!columnDefinitions || columnDefinitions.length === 0) {
    return false;
  }

  return columnDefinitions.some(col => {
    // Only check visible columns
    const isVisible = Object.keys(columnVisibilityModel).length === 0
      || columnVisibilityModel[col.value] !== false;

    return col.field === FIELD_USER && isVisible;
  });
};

/**
 * Hook to fetch reference tables for visible columns
 * Returns the list of required table IDs to fetch
 *
 * @param {Array} columnDefinitions - Array of column definition objects
 * @param {Object} columnVisibilityModel - Object mapping column names to visibility boolean
 * @returns {Array} Array of table IDs that need to be fetched
 */
export const useRequiredTableIds = (columnDefinitions, columnVisibilityModel) => {
  // Import React hooks inside the function
  const { useMemo } = require('react');

  return useMemo(() => {
    return getRequiredTableIds(columnDefinitions, columnVisibilityModel);
  }, [columnDefinitions, columnVisibilityModel]);
};
